import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback, useRef, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
    DicomBase,
    GenerationModelDetails,
    ObjectStatus,
} from '~/domain/dicom/dicomBase';
import {
    ImportResult,
    InterpolatedImageInfo,
} from '~/domain/services/dicomProcessor';
import {
    dicomProcessorState,
    filesProcessedState,
    filesToProcessState,
} from '~/state/atoms/dicomProcessorAtoms';
import { imageListVersionState } from '~/state/atoms/image/imageAtoms';
import {
    interpolationAcknowledgementState,
    setPendingInterpolationResolver,
} from '~/state/atoms/interpolationAtoms';
import { patientRepositoryState } from '~/state/atoms/patientAtoms';
import { exportableItemsCountState } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { createSkipReport, processFile } from '~/utils/io/files/FileHandler';
import { emitSkipReportToasts } from '~/utils/io/files/skipReportToasts';
import usePatientGuard, {
    PatientGuardResult,
} from '~/hooks/patient/usePatientGuard';
import useUnloadPatient from '~/hooks/patient/useUnloadPatient';
import { MultiPatientConflictAction } from '~/components/Dialogs/MultiPatientConflictDialog';
import { useSessionOrNull } from '~/contexts/SessionContext';

type ProcessDicomFilesParams = {
    onFileProcessed: (file: DicomBase) => void;
};

/** State exposed to consumers so they can render the conflict dialog. */
export type PatientConflictState = {
    open: boolean;
    loadedPatientId: string;
    incomingPatientIds: string[];
    hasUnsavedChanges: boolean;
    clinicalUse: boolean;
};

export default function useProcessDicomFiles(
    params: ProcessDicomFilesParams = { onFileProcessed: () => {} }
) {
    const dicomProcessor = useRecoilValue(dicomProcessorState);
    const patientRepository = useRecoilValue(patientRepositoryState);
    const setFilesToProcess = useSetRecoilState(filesToProcessState);
    const setFilesProcessed = useSetRecoilState(filesProcessedState);
    const setExportableItemsCount = useSetRecoilState(
        exportableItemsCountState
    );
    const setImageListVersion = useSetRecoilState(imageListVersionState);
    const setInterpolationAcknowledgement = useSetRecoilState(
        interpolationAcknowledgementState
    );

    const { checkPatientConflict } = usePatientGuard();
    const { unloadCurrentPatient } = useUnloadPatient();
    const session = useSessionOrNull();

    // Promise-based dialog state: when a conflict is detected we open
    // the dialog and park a resolver here so the processing flow can
    // await the user's decision.
    const [patientConflict, setPatientConflict] =
        useState<PatientConflictState>({
            open: false,
            loadedPatientId: '',
            incomingPatientIds: [],
            hasUnsavedChanges: false,
            clinicalUse: true,
        });
    const conflictResolverRef = useRef<
        ((action: MultiPatientConflictAction) => void) | null
    >(null);

    /** Called by the consumer's dialog component when the user picks an action. */
    const resolvePatientConflict = useCallback(
        (action: MultiPatientConflictAction) => {
            setPatientConflict((prev) => ({ ...prev, open: false }));
            conflictResolverRef.current?.(action);
            conflictResolverRef.current = null;
        },
        []
    );

    function resetProcessingState() {
        setFilesProcessed(0);
        setFilesToProcess(undefined);
    }

    /**
     * Shows the conflict dialog and returns a promise that resolves
     * to the user's chosen action.
     */
    function awaitConflictResolution(
        guardResult: Extract<PatientGuardResult, { action: 'conflict' }>
    ): Promise<MultiPatientConflictAction> {
        return new Promise<MultiPatientConflictAction>((resolve) => {
            conflictResolverRef.current = resolve;
            setPatientConflict({
                open: true,
                loadedPatientId: guardResult.loadedPatientId,
                incomingPatientIds: guardResult.incomingPatientIds,
                hasUnsavedChanges: guardResult.hasUnsavedChanges,
                clinicalUse: guardResult.clinicalUse,
            });
        });
    }

    /**
     * Opens the blocking acknowledgement dialog and resolves once
     * the user clicks Acknowledge. Interpolation is a clinically
     * significant transformation, so we require an explicit, non-
     * dismissible confirmation rather than a transient snackbar.
     */
    function awaitInterpolationAcknowledgement(
        images: InterpolatedImageInfo[]
    ): Promise<void> {
        return new Promise<void>((resolve) => {
            setPendingInterpolationResolver(resolve);
            setInterpolationAcknowledgement({ open: true, images });
        });
    }

    async function handleFilesProcessed(options?: {
        allowMultiplePatients?: boolean;
    }): Promise<ImportResult | undefined> {
        try {
            // dicomProcessor.toRepository is synchronous: it calls
            // patient.addStructureSet (and similar) which fires
            // markChanged when a tracker is attached. Wrapping in
            // tracker.batch groups all insertions into a single
            // coalesced changeset and prevents the "Mutation outside
            // command()/batch()" throw that occurs during incremental
            // loads (e.g. auto-segmentation results) when the tracker
            // is already attached to the patient.
            let result: ImportResult | undefined;
            const work = () => {
                result = dicomProcessor.toRepository(
                    patientRepository,
                    options
                );
            };
            if (session) {
                session.context.tracker.batch(work);
            } else {
                work();
            }

            // Block on user acknowledgement of interpolated series
            // before returning. The image list refresh below runs
            // only after acknowledgement so the new images do not
            // appear behind the modal. The processing overlay is
            // dismissed first because it renders at z-index 4000
            // and would otherwise sit above the dialog.
            if (result?.interpolatedImages.length) {
                resetProcessingState();
                await awaitInterpolationAcknowledgement(
                    result.interpolatedImages
                );
            }

            // Notify user about series that could not be reconstructed
            if (result?.skippedSeries.length) {
                for (const info of result.skippedSeries) {
                    const description = info.seriesDescription
                        ? `"${info.seriesDescription}"`
                        : 'Image series';
                    enqueueSnackbar(
                        `${description} could not be reconstructed: ${info.reason}`,
                        {
                            variant: 'warning',
                        }
                    );
                }
            }

            setImageListVersion((v) => v + 1);
            return result;
        } catch (error: Error | unknown) {
            if (error instanceof Error) {
                handleError(error, `Failed to process files: ${error.message}`);
            } else {
                handleError(error, 'Failed to process files');
            }
        } finally {
            resetProcessingState();
        }
    }

    function checkForExistingRTStructs(sliceData: DicomBase): boolean {
        const existingPatient = patientRepository.getByPatientId(
            sliceData.datasetWrapper.PatientID
        );
        if (existingPatient) {
            const existingStructureSet =
                existingPatient.getStructureSetWithSopInstanceUid(
                    sliceData.sopInstanceUid
                );
            if (existingStructureSet) {
                return true;
            } else {
                return false;
            }
        }
        return false;
    }

    function checkForExistingRTDoses(sliceData: DicomBase): boolean {
        const existingPatient = patientRepository.getByPatientId(
            sliceData.datasetWrapper.PatientID
        );
        if (existingPatient) {
            const existingDose = existingPatient.getDoseWithSopInstanceUid(
                sliceData.sopInstanceUid
            );
            if (existingDose) {
                return true;
            } else {
                return false;
            }
        }
        return false;
    }

    async function processFiles(
        files: File[],
        objectStatus: ObjectStatus,
        model?: GenerationModelDetails,
        options?: { allowMultiplePatients?: boolean }
    ) {
        // Reset both counters so this phase is self-contained.
        // `filesToProcess` is overwritten anyway, but `filesProcessed`
        // would otherwise carry leftover increments from a prior phase
        // (e.g. import → composite-result back-to-back) and produce a
        // numerator larger than the denominator in the overlay.
        setFilesProcessed(0);
        setFilesToProcess(files.length);
        try {
            const skipReport = createSkipReport();
            for await (const file of files) {
                const dicomData = await processFile(
                    file,
                    dicomProcessor,
                    objectStatus,
                    model,
                    skipReport
                );
                if (dicomData) {
                    if (checkForExistingRTStructs(dicomData)) {
                        enqueueSnackbar(
                            'This RTStruct has already been loaded.',
                            {
                                variant: 'warning',
                            }
                        );
                        continue;
                    }
                    if (checkForExistingRTDoses(dicomData)) {
                        enqueueSnackbar(
                            'This RTDose has already been loaded.',
                            {
                                variant: 'warning',
                            }
                        );
                        continue;
                    }
                    params.onFileProcessed(dicomData);
                }
                setFilesProcessed((prevFilesLoaded) => prevFilesLoaded + 1);
            }

            emitSkipReportToasts(skipReport);

            // Check for patient conflict before committing to the
            // repository. The files have been parsed into
            // dicomProcessor but not yet added to patientRepository.
            // Skip if the caller already resolved the conflict
            // (e.g. early dismiss in the connector import flow).
            const incomingPatientIds = dicomProcessor.getPatientIds();
            const guardResult = options?.allowMultiplePatients
                ? ({ action: 'allow' } as const)
                : checkPatientConflict(incomingPatientIds);

            if (guardResult.action === 'conflict') {
                // Dismiss the processing overlay so the conflict
                // dialog is not hidden behind it (FilesProcessed
                // renders at z-index 4000).
                resetProcessingState();

                const userAction = await awaitConflictResolution(guardResult);

                if (userAction === 'discard') {
                    // Drop the parsed incoming patient data without
                    // committing. Preserve the currently loaded
                    // patient's processed state to avoid allowing
                    // duplicate imports after discard.
                    guardResult.incomingPatientIds.forEach((patientId) => {
                        dicomProcessor.removePatientData(patientId);
                    });
                    resetProcessingState();
                    return undefined;
                }

                if (userAction === 'dismiss') {
                    // Non-clinical: user chose to load both patients.
                    // Commit with relaxed multi-patient guard.
                    return handleFilesProcessed({
                        allowMultiplePatients: true,
                    });
                }

                // 'switch' — unload existing patient, then commit.
                // Preserve the DicomProcessor state because it still
                // holds the incoming patient data that toRepository()
                // needs to commit.
                unloadCurrentPatient({ preserveDicomProcessor: true });

                // Remove the old patient's data from the processor.
                // The processor accumulates all parsed patients; we
                // only want to commit the incoming one(s).
                dicomProcessor.removePatientData(guardResult.loadedPatientId);
            }

            return handleFilesProcessed(options);
        } catch (error) {
            handleError(error, 'Error processing files');
            resetProcessingState();
        }
    }

    const processImportedFiles = async (
        files: File[],
        options?: { allowMultiplePatients?: boolean }
    ) => {
        return processFiles(files, ObjectStatus.IMPORTED, undefined, options);
    };

    const processCreatedFiles = async (
        files: File[],
        model?: GenerationModelDetails
    ) => {
        const processed = await processFiles(files, ObjectStatus.NEW, model);
        setExportableItemsCount((prevCount) => prevCount + 1);
        return processed;
    };

    return {
        processImportedFiles,
        processCreatedFiles,
        patientConflict,
        resolvePatientConflict,
    };
}
